import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiDelete, apiGet, apiPost, apiPut } from '../lib/api-client';
import { apiError } from '../lib/utils';
import { formatMoney, poundsToPence, penceToPoundsInput } from '../lib/money';
import { useAuth } from '../contexts/AuthContext';
import { BarChart } from '../components/ui/BarChart';
import { Button } from '../components/ui/Button';
import { Dialog } from '../components/ui/Dialog';
import { PageHead } from '../components/ui/chrome';
import { ColumnFilterInputs, ColumnFilterToggle, useColumnSearch } from '../components/ui/ColumnSearch';
import { Field, Input, Select } from '../components/ui/Input';
import { customerChoices, useFreshRows } from '../lib/select-choices';

const CATEGORIES = [
  { id: 'bau', label: 'BAU' },
  { id: 'capital', label: 'Capital' },
  { id: 'project', label: 'Project' },
  { id: 'cloud', label: 'Cloud and hosting' },
  { id: 'rd', label: 'R&D' },
];

interface Budget {
  id: string;
  name: string;
  category: string;
  audience: 'customer' | 'internal';
  customerId?: string;
  amountPence: number;
  currency: string;
  from: string;
  to: string;
  notes?: string;
}

interface BudgetForm {
  id?: string;
  name: string;
  category: string;
  audience: 'customer' | 'internal';
  customerId: string;
  amount: string;
  currency: string;
  from: string;
  to: string;
  notes: string;
}

const EMPTY: BudgetForm = { name: '', category: 'bau', audience: 'internal', customerId: '', amount: '', currency: 'GBP', from: '', to: '', notes: '' };

export function BudgetsPage() {
  const { can } = useAuth();
  const editable = can('budgets', 'edit');
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ['budgets'], queryFn: () => apiGet<Budget[]>('/api/budgets') });
  const [customers, reloadCustomers] = useFreshRows<{ id: string; code?: string; name: string }>('/api/customers');
  const [editing, setEditing] = useState<BudgetForm | null>(null);
  const [error, setError] = useState('');
  const columnSearch = useColumnSearch();
  const allRows = query.data || [];
  const names = new Map(customers.map((row) => [row.id, row.name]));
  const rows = allRows.filter((row) => columnSearch.matches({
    name: row.name,
    category: CATEGORIES.find((item) => item.id === row.category)?.label || row.category,
    audience: row.audience === 'customer' ? names.get(row.customerId || '') || 'Customer' : 'Internal',
    amount: formatMoney(row.amountPence, row.currency),
    from: row.from,
    to: row.to,
  }));
  const chart = CATEGORIES.flatMap((category) => {
    const currencies = [...new Set(allRows.filter((row) => row.category === category.id).map((row) => row.currency || 'GBP'))];
    return currencies.map((currency) => ({
      label: currency === 'GBP' ? category.label : `${category.label} ${currency}`,
      value: allRows.filter((row) => row.category === category.id && (row.currency || 'GBP') === currency).reduce((sum, row) => sum + row.amountPence, 0),
      currency,
    }));
  }).filter((row) => row.value > 0);

  async function save() {
    if (!editing) return;
    setError('');
    const body = {
      name: editing.name,
      category: editing.category,
      audience: editing.audience,
      customerId: editing.audience === 'customer' ? editing.customerId : '',
      amountPence: poundsToPence(editing.amount),
      currency: editing.currency,
      from: editing.from,
      to: editing.to,
      notes: editing.notes,
    };
    try {
      if (editing.id) await apiPut(`/api/budgets/${editing.id}`, body);
      else await apiPost('/api/budgets', body);
      setEditing(null);
      await queryClient.invalidateQueries({ queryKey: ['budgets'] });
    } catch (err) {
      setError(apiError(err, 'Save failed'));
    }
  }

  async function remove(id: string) {
    await apiDelete(`/api/budgets/${id}`);
    await queryClient.invalidateQueries({ queryKey: ['budgets'] });
  }

  return (
    <div className="space-y-4">
      <PageHead crumbs={[{ label: 'Payable' }, { label: 'Budgets' }]} title="Budgets" subtitle="A budget is a plan. It does not take bills off the payable report." actions={editable ? <Button onClick={() => { setError(''); setEditing({ ...EMPTY }); }}>New budget</Button> : null} />
      <div className="rounded-lg border border-[var(--bo-border)] bg-[var(--bo-surface)] p-4">
        <h2 className="mb-3 text-sm font-semibold">Budget by category</h2>
        <BarChart rows={chart} />
      </div>
      <div className="overflow-x-auto rounded-lg border border-[var(--bo-border)]">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase text-[var(--bo-faint)]">
              <th className="px-3 py-2">Name</th>
              <th className="px-3 py-2">Category</th>
              <th className="px-3 py-2">For</th>
              <th className="px-3 py-2">Amount</th>
              <th className="px-3 py-2">From</th>
              <th className="px-3 py-2">To</th>
              <th className="px-3 py-2 text-right"><ColumnFilterToggle open={columnSearch.open} onClick={columnSearch.toggle} /></th>
            </tr>
            {columnSearch.open ? <ColumnFilterInputs columns={[{ key: 'name', label: 'Name' }, { key: 'category', label: 'Category' }, { key: 'audience', label: 'For' }, { key: 'amount', label: 'Amount' }, { key: 'from', label: 'From' }, { key: 'to', label: 'To' }]} draft={columnSearch.draft} onChange={columnSearch.set} trailing /> : null}
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id} className="border-t border-[var(--bo-border)]">
                <td className="px-3 py-2">{row.name}</td>
                <td className="px-3 py-2">{CATEGORIES.find((item) => item.id === row.category)?.label || row.category}</td>
                <td className="px-3 py-2">{row.audience === 'customer' ? names.get(row.customerId || '') || 'Customer' : 'Internal'}</td>
                <td className="px-3 py-2 tabular-nums">{formatMoney(row.amountPence, row.currency)}</td>
                <td className="px-3 py-2">{row.from}</td>
                <td className="px-3 py-2">{row.to}</td>
                <td className="px-3 py-2 text-right">
                  {editable ? (
                    <>
                      <button type="button" className="text-xs text-[#895CF6] underline" onClick={() => setEditing({ id: row.id, name: row.name, category: row.category, audience: row.audience, customerId: row.customerId || '', amount: penceToPoundsInput(row.amountPence), currency: row.currency, from: row.from, to: row.to, notes: row.notes || '' })}>Edit</button>
                      <button type="button" className="ml-3 text-xs text-red-600 underline" onClick={() => void remove(row.id)}>Delete</button>
                    </>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!rows.length ? <p className="px-4 py-8 text-sm text-[var(--bo-muted)]">No budgets yet.</p> : null}
      </div>
      {editing ? (
        <Dialog title={editing.id ? 'Edit budget' : 'New budget'} onClose={() => setEditing(null)} footer={<Button onClick={() => void save()}>Save</Button>}>
          {error ? <p className="mb-3 text-sm text-red-600">{error}</p> : null}
          <div className="grid gap-3">
            <Field label="Name"><Input value={editing.name} onChange={(event) => setEditing({ ...editing, name: event.target.value })} /></Field>
            <Field label="Category">
              <Select value={editing.category} onChange={(event) => setEditing({ ...editing, category: event.target.value })}>
                {CATEGORIES.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}
              </Select>
            </Field>
            <Field label="Who it is for">
              <Select value={editing.audience} onChange={(event) => setEditing({ ...editing, audience: event.target.value === 'customer' ? 'customer' : 'internal' })}>
                <option value="internal">Internal</option>
                <option value="customer">Customer</option>
              </Select>
            </Field>
            {editing.audience === 'customer' ? (
              <Field label="Customer">
                <Select searchable choices={customerChoices(customers)} onOpen={reloadCustomers} value={editing.customerId} onChange={(event) => setEditing({ ...editing, customerId: event.target.value })} />
              </Field>
            ) : null}
            <Field label="Amount"><Input value={editing.amount} onChange={(event) => setEditing({ ...editing, amount: event.target.value })} /></Field>
            <Field label="Currency">
              <Select value={editing.currency} onChange={(event) => setEditing({ ...editing, currency: event.target.value })}>
                <option value="GBP">GBP</option>
                <option value="EUR">EUR</option>
                <option value="USD">USD</option>
              </Select>
            </Field>
            <Field label="From"><Input type="date" value={editing.from} onChange={(event) => setEditing({ ...editing, from: event.target.value })} /></Field>
            <Field label="To"><Input type="date" value={editing.to} onChange={(event) => setEditing({ ...editing, to: event.target.value })} /></Field>
            <Field label="Notes"><Input value={editing.notes} onChange={(event) => setEditing({ ...editing, notes: event.target.value })} /></Field>
          </div>
        </Dialog>
      ) : null}
    </div>
  );
}
